import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Paper,
    CircularProgress,
    Alert,
    Divider,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HighlightIcon from '@mui/icons-material/Highlight';
import aiService from '../../../../../services/api/ai.service';
import styles from './ExplainWhyDialog.module.scss';
import AIIcon from '../../../../../assets/svgs/AI-Icon.svg';
import useAccountId from '../../../../../hooks/useAccountId';

const ExplainWhyDialog = ({
    open,
    onClose,
    fieldAccessor,
    fieldLabel,
    suggestedValue,
    originalPrompt,
    formContext,
    facilityId
}) => {
    const [explanation, setExplanation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { accountId: accountIdFromHook } = useAccountId();
    
    const fetchExplanation = useCallback(async () => {
        setLoading(true);
        setError(null);
        setExplanation(null);

        try {
            
            const accountId = accountIdFromHook;
            
            const response = await aiService.explainFieldSuggestion({
                fieldAccessor,
                fieldLabel,
                suggestedValue,
                originalPrompt,
                formContext,
                facilityId,
                accountId
            });

            setExplanation(response);
        } catch (err) {
            console.error('Error fetching explanation:', err);
            setError(err.message || 'Failed to get explanation');
        } finally {
            setLoading(false);
        }
    }, [fieldAccessor, fieldLabel, suggestedValue, originalPrompt, formContext, facilityId, accountIdFromHook]);

    useEffect(() => {
        if (open && fieldAccessor && suggestedValue && originalPrompt) {
            fetchExplanation();
        }
    }, [open, fieldAccessor, suggestedValue, originalPrompt, fetchExplanation]);

    const handleClose = () => {
        setExplanation(null);
        setError(null);
        onClose();
    };


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                className: styles.explainDialog
            }}
        >
            <DialogTitle className={styles.dialogTitle}>
                <Box display="flex" alignItems="center" gap={1}>
                    <AIIcon color="primary" />
                    <Typography variant="h6" component="div">
                        Why was this value suggested?
                    </Typography>
                </Box>
                <Button
                    onClick={handleClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                    size="small"
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent className={styles.dialogContent}>
                {/* Field Information */}
                <Paper className={styles.fieldInfoPaper} elevation={1}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                        Field: {fieldLabel}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        <strong>Suggested Value:</strong> {suggestedValue}
                    </Typography>
                </Paper>

                <Divider sx={{ my: 2 }} />

                {/* Loading State */}
                {loading && (
                    <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                            Analyzing suggestion...
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, textAlign: 'center' }}>
                            This may take a few moments
                        </Typography>
                    </Box>
                )}

                {/* Error State */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Explanation Content */}
                {explanation && (
                    <Box>
                        {/* Main Explanation */}
                        <Paper className={styles.explanationPaper} elevation={1}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <HighlightIcon color="primary" fontSize="small" />
                                <Typography variant="subtitle2" color="primary">
                                    AI Explanation
                                </Typography>
                            </Box>
                            <Typography variant="body2" className={styles.explanationText}>
                                {explanation.reasoning}
                            </Typography>
                        </Paper>

                        {/* Relevant Lines from Prompt */}
                        {explanation.relevantLines && explanation.relevantLines.length > 0 && (
                            <Paper className={styles.promptPaper} elevation={1}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    Key information from your prompt:
                                </Typography>
                                <Box className={styles.relevantLinesContainer}>
                                    {explanation.relevantLines.map((line, index) => (
                                        <Box key={index} className={styles.relevantLineItem}>
                                            <Typography variant="body2" className={styles.relevantLineText}>
                                                "{line.text}"
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" className={styles.relevantLineReason}>
                                                {line.relevance}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        )}

                        {/* Confidence Score */}
                        {explanation.confidence && (
                            <Box display="flex" alignItems="center" gap={1} mt={2} mb={2}>
                                <Typography variant="body2" color="textSecondary">
                                    Confidence:
                                </Typography>
                                <Chip
                                    label={`${Math.round(explanation.confidence * 100)}%`}
                                    color={explanation.confidence > 0.8 ? 'success' : explanation.confidence > 0.6 ? 'warning' : 'error'}
                                    size="small"
                                />
                            </Box>
                        )}

                        {/* Alternative Interpretations */}
                        {/* {explanation.alternativeInterpretations && explanation.alternativeInterpretations.length > 0 && (
                            <Paper className={styles.contextPaper} elevation={1}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    Other Possible Values:
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {explanation.alternativeInterpretations.map((alt, index) => (
                                        <Chip
                                            key={index}
                                            label={alt}
                                            variant="outlined"
                                            size="small"
                                            color="secondary"
                                        />
                                    ))}
                                </Box>
                            </Paper>
                        )} */}

                        {/* Context Influence */}
                        {explanation.contextInfluence && (
                            <Paper className={styles.contextPaper} elevation={1}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    How Context Influenced This Decision:
                                </Typography>
                                <Typography variant="body2" className={styles.contextText}>
                                    {explanation.contextInfluence}
                                </Typography>
                            </Paper>
                        )}

                        {/* Additional Context */}
                        {explanation.context && (
                            <Paper className={styles.contextPaper} elevation={1}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    Additional Context:
                                </Typography>
                                <Typography variant="body2" className={styles.contextText}>
                                    {explanation.context}
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions className={styles.dialogActions}>
                <Button onClick={handleClose} variant="outlined">
                    Close
                </Button>
                {explanation && (
                    <Button 
                        onClick={fetchExplanation} 
                        variant="contained" 
                        color="primary"
                        disabled={loading}
                    >
                        Refresh Explanation
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ExplainWhyDialog;
